import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { themeGet } from '@styled-system/theme-get';

import LoadingComponent from '../../shared/LoadingComponent';
import ProgressBarComponent from '../../shared/ProgressBarComponent';
import TileWallComponent from './components/TileWallComponent.jsx';
import WelcomeBanner from '../../shared/WelcomeBanner/index.jsx';
import { InternalChatBot } from '../../shared/InternalChatBot/InternalChatBot.jsx';
import PotentialUserModal from './components/PotentialUserModal.jsx';
import { Container, LoadingContainer } from './components/StyledComponents';
import Modal from '../../shared/Modal';
import Text from '../../shared/Text';
import {
  suggestedLanguages,
  languages,
} from '../../translations/en/supportedLanguages.json';
import { UserViewCampaigns } from './components/UserViewCampaigns/UserViewCampaigns.jsx';
import { useBannerData } from './hooks/useBannerData';
import { MomentsContainer } from '../moments/components/MomentsContainer.jsx';
import { selectCampaign } from '../../lib/redux/selectedCampaign/selectedCampaignSlice';

export const ActivityBoardContainer = ({
  addTiles,
  demo,
  user,
  loadTiles,
  navigateTo,
  tiles,
  setUser,
  setTiles,
}) => {
  const [loading, setLoading] = useState(true);
  const [loadingMoreTiles, setLoadingMoreTiles] = useState(false);
  const { t } = useTranslation('activityBoard');
  const { introMessage, name, primaryColor } = useBannerData();

  const { userLanguage } = useSelector(state => state.currentUser);
  const { selectedCampaignUuid, newAirbo } = useSelector(state => ({
    selectedCampaignUuid: state.selectedCampaign.uuid,
    newAirbo: state.demoData.newAirbo,
  }));

  const isUserLangEn = userLanguage === 'en';

  const language = [...suggestedLanguages, ...languages].find(
    ({ code }) => userLanguage === code,
  ) || { name: '' };

  useEffect(() => {
    const trackUserVisitedBoardEvent = () => {
      const mixpanelData = {
        userName: user.name,
        boardName: window.Airbo.Utils.currentBoard().name,
        isPublic: window.Airbo.Utils.currentBoard().is_public,
      };

      window.Airbo.Utils.ping('User visited board', mixpanelData);
    };

    const openPotentialUserModal = () => {
      Modal({
        component: (
          <PotentialUserModal demoName={demo.name} setUser={setUser} />
        ),
        padding: '24px',
        width: '394px',
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    };

    const launchWelcomeModals = () => {
      window.Airbo.BoardRedirect.init();
      if (user.isPotentialUser) {
        openPotentialUserModal();
      }
    };

    const makeLoadTiles = () => {
      setLoading(true);
      loadTiles({
        loadAll: true,
        perPage: 16,
        paginateState: {
          completeTilesPage: 1,
          incompleteTilesPage: 1,
        },
        campaignUuid: selectedCampaignUuid,
        success: resp => {
          setTiles(resp.tiles);
          setLoading(false);
        },
        error: () => {
          setLoading(false);
        },
      });
    };

    trackUserVisitedBoardEvent();

    /*
      this contidional is to avoid doing 1 extra request
      if the board is a new airbo board but still doest have a campaignUuid set
    */
    if (newAirbo && selectedCampaignUuid) {
      makeLoadTiles();
    }

    if (newAirbo && !selectedCampaignUuid && !demo.hasActiveCampaignWithTiles) {
      setLoading(false);
    }

    if (!newAirbo) {
      makeLoadTiles();
    }

    launchWelcomeModals();
  }, [
    demo.hasActiveCampaignWithTiles,
    demo.name,
    loadTiles,
    newAirbo,
    selectedCampaignUuid,
    setTiles,
    setUser,
    user.isPotentialUser,
    user.name,
  ]);

  const loadMoreTiles = () => {
    setLoadingMoreTiles(true);
    loadTiles({
      perPage: 16,
      campaignUuid: selectedCampaignUuid,
      success: resp => {
        addTiles(resp.tiles);
        setLoadingMoreTiles(false);
      },
      error: () => {
        setLoadingMoreTiles(false);
      },
    });
  };

  const openTileModal = id => {
    const baseRoute =
      demo.isPublic && user.isGuestUser
        ? `/ard/${demo.publicSlug}/tiles`
        : '/tiles';
    navigateTo(`${baseRoute}?tile_id=${id}`);
  };

  const openFirstTile = () => {
    if (tiles.incomplete.order.length > 0) {
      const incompleteTile = tiles.incomplete.order[0];
      openTileModal(incompleteTile);
    } else {
      const completeTile = tiles.complete.order[0];
      openTileModal(completeTile);
    }
  };

  const showWelcomeBanner = demo.newAirbo ? demo.hasActiveCampaign : true;
  const hasTilesToDisplay = !tiles.paginateState.noTilesToDisplay;
  const showNoActiveCampaignMsg =
    demo.newAirbo &&
    !loading &&
    !demo.hasActiveCampaignWithTiles &&
    !selectedCampaignUuid;

  return (
    <Container id="user-preview">
      {demo.newAirbo && <UserViewCampaigns isMoments={demo.moments} />}
      {showWelcomeBanner && (
        <WelcomeBanner
          introMessage={introMessage}
          primaryColor={primaryColor}
          name={name}
          handleOnClick={openFirstTile}
        />
      )}
      <Wrapper>
        <ProgressBarComponent
          canSuggestTile={user.canSuggestTile}
          campaignUuid={selectedCampaignUuid}
        />
      </Wrapper>
      {loading && (
        <LoadingContainer>
          {!isUserLangEn && (
            <LoadingTranslations>
              {t('loadingTranslations', { languageName: language.name })}
            </LoadingTranslations>
          )}
          <LoadingComponent marginTop="20px" />
        </LoadingContainer>
      )}
      {!loading && hasTilesToDisplay && (
        <TileWallComponent
          tiles={tiles}
          openTileModal={openTileModal}
          loadMoreTiles={loadMoreTiles}
          loadingMoreTiles={loadingMoreTiles}
          allTilesDisplayed={tiles.paginateState.allTilesDisplayed}
        />
      )}
      {!loading && showNoActiveCampaignMsg && (
        <NoActiveCampaignText>{t('noActiveCampaign')}</NoActiveCampaignText>
      )}
    </Container>
  );
};

ActivityBoardContainer.propTypes = {
  loadTiles: PropTypes.func,
  setTiles: PropTypes.func,
  setTilesPaginationState: PropTypes.func,
  addTiles: PropTypes.func,
  setUser: PropTypes.func,
  navigateTo: PropTypes.func,
  tiles: PropTypes.shape({
    complete: PropTypes.object,
    incomplete: PropTypes.object,
    paginateState: PropTypes.object,
  }),
  demo: PropTypes.object,
  user: PropTypes.object,
};

const ActivityBoard = props => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { selectedCampaignUuid } = useSelector(state => ({
    selectedCampaignUuid: state.selectedCampaign.uuid,
  }));

  useEffect(() => {
    const updateCampaign = () => {
      setIsLoading(true);
      const newUrlParams = new URLSearchParams(window.location.search);
      const newCurrentCampaignId = newUrlParams.get('campaign_id');

      if (!newCurrentCampaignId) {
        dispatch(selectCampaign({}));
      }
      setIsLoading(false);
    };

    updateCampaign();

    window.addEventListener('popstate', updateCampaign);

    return () => {
      window.removeEventListener('popstate', updateCampaign);
    };
  }, [dispatch, selectedCampaignUuid]);

  if (isLoading) return <LoadingComponent />;

  const shouldShowMoments =
    !selectedCampaignUuid && props.demo.newAirbo && props.demo.moments;

  return (
    <>
      {props.demo.employeeWebAssistantEnabledUuid && (
        <InternalChatBot
          assistantUuid={props.demo.employeeWebAssistantEnabledUuid}
        />
      )}
      {shouldShowMoments ? (
        <MomentsContainer {...props} />
      ) : (
        <ActivityBoardContainer {...props} />
      )}
    </>
  );
};

export default ActivityBoard;

ActivityBoard.propTypes = {
  demo: PropTypes.object,
};

const LoadingTranslations = styled(Text)`
  align-self: center;
  justify-self: center;

  margin: 8px;
`;

const NoActiveCampaignText = styled(Text)`
  align-self: center;
  justify-self: center;

  margin: 8px;
`;

const Wrapper = styled.div`
  position: fixed;
  display: flex;
  width: 100%;
  bottom: 0;
  height: 60px;
  background: white;
  z-index: 1;
  box-shadow: 4px 0px 8px rgba(37, 40, 43, 0.14);
  ${themeGet('mediaQueries.forMediumUp')} {
    margin: 24px auto;
    position: relative;
    background: transparent;
    z-index: initial;
    box-shadow: none;
  }
`;
